import React, { useCallback, useEffect, useState } from 'react';
import { usePost } from './API';

type RequestDetails = {
  program: string,
  clientName?: string,
  clientId: string,
  redirectHost: string,
  /** What this user could grant here — derived from their permissions, not from the client's request. */
  offeredScopes: string[],
  /** The client asked for something real, but this user holds none of the permissions. */
  nothingToGrant: boolean,
};

const SCOPE_LABELS: Record<string, { title: string, detail: string }> = {
  'mcp:readDistroConfig': {
    title: 'Read this program\'s configuration',
    detail: 'Surveys, branches, dashboards and validation. No applicant data.',
  },
  'mcp:editDistroConfig': {
    title: 'Save changes to a branch and submit them for review',
    detail: 'Unpublished changes only. Publishing still happens in Distro, where you review what changed.',
  },
  'mcp:publishDistroConfig': {
    title: 'Publish configuration changes',
    detail: 'Makes changes live for applicants, without anyone reviewing them first.',
  },
};

/**
 * Ticked when the screen opens. Everything else starts off, so an agent gets the scope
 * that cannot change anything unless the person reading this deliberately widens it —
 * clients ask for everything they might ever want, and the request should not be the
 * decision.
 */
const DEFAULT_ON = ['mcp:readDistroConfig'];

/**
 * The consent screen for an MCP agent connecting over OAuth.
 *
 * The user is genuinely logged in by the time they get here, so what is being attacked is
 * not whether they authenticate but what they believe they are approving. Hence: the client
 * named with its metadata URL, the host the code will be delivered to shown verbatim, and the scopes as
 * checkboxes rather than a bundle — a client that always asks for write can then still be
 * connected read-only, which is the only place a user gets that choice. No MCP client
 * offers it.
 */
export function OAuthConsentPage() {
  const getDetails = usePost('/oauth/authorize/request');
  const approve = usePost('/oauth/authorize/approve');
  const deny = usePost('/oauth/authorize/deny');

  const request = new URLSearchParams(window.location.search).get('request') || '';
  const [details, setDetails] = useState<RequestDetails | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A consent screen inside someone else's frame is a click away from being a grant the
  // user never saw. The server should send frame-ancestors too; this is the half we own.
  const framed = window.top !== window.self;

  const finish = useCallback(async (result: { error: string } | { redirect: string } | undefined) => {
    if (!result || !('redirect' in result) || !result.redirect) {
      setBusy(false);
      setError('Something went wrong finishing this connection. Start it again from your client.');
      return;
    }
    // Belt and braces against a `javascript:` destination. The server refuses to accept
    // one into a client's metadata, but this is the line that would execute it in this
    // origin, in a logged-in session, so it does not take that on trust.
    if (!/^https?:\/\//i.test(result.redirect)) {
      setBusy(false);
      setError('This connection asked to send you somewhere unsafe. Nothing was approved.');
      return;
    }
    // A full navigation, not a router push: the destination belongs to the client, and the
    // server built it from a redirect_uri the client had already published.
    window.location.replace(result.redirect);
  }, []);

  useEffect(() => {
    if (framed || !request) {
      setError('This link is not valid. Start the connection again from your client.');
      return;
    }
    (async () => {
      const result = await getDetails({ request });
      if (!result || 'error' in result) {
        setError('This request has expired. Start the connection again from your client.');
        return;
      }
      setDetails(result);
      setChosen(result.offeredScopes.filter(s => DEFAULT_ON.includes(s)));
      // Nothing to offer is still an answer the client needs. It is waiting on a loopback
      // port and will hang until it times out otherwise.
      if (result.nothingToGrant) finish(await deny({ request }));
    })();
  }, [request, framed, getDetails, deny, finish]);

  if (error) {
    return (
      <Shell>
        <p className="text-base text-gray-700">{error}</p>
      </Shell>
    );
  }

  if (!details) {
    return <Shell><p className="text-base text-gray-500">Loading…</p></Shell>;
  }

  const client = details.clientName || details.clientId;
  // A client chooses its own name; its metadata URL must remain visible beside it.
  const clientUrl = new URL(details.clientId).href;

  if (details.nothingToGrant) {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold text-gray-900">Nothing to connect</h1>
        <p className="mt-2 text-sm text-gray-600">
          {client} asked for access to {details.program}, but your account does not have
          permissions any of it would use. Ask an administrator if you think it should.
        </p>
        <p className="mt-4 text-sm text-gray-500">Telling {client} so…</p>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-2xl font-semibold text-gray-900">
        Connect {client} to {details.program}?
      </h1>
      <p className="mt-2 text-sm text-gray-600">
        Client address:
        <span dir="ltr" className="block break-all font-mono text-gray-900">{clientUrl}</span>
        The app supplies its own name. Check this address before allowing access.
      </p>
      <p className="mt-2 text-sm text-gray-600">
        It acts as you, so it can only do what your own permissions already allow — this list
        is what you could do yourself, not what {client} asked for. Everything beyond reading
        is off unless you turn it on, and adding it later means connecting again.
      </p>

      <ul className="mt-6 space-y-3">
        {details.offeredScopes.map(scope => (
          <li key={scope} className="flex gap-3 rounded border border-gray-200 p-3">
            <input
              id={scope}
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={chosen.includes(scope)}
              onChange={e => setChosen(prev =>
                e.target.checked ? [...prev, scope] : prev.filter(s => s !== scope))}
            />
            <label htmlFor={scope} className="cursor-pointer">
              <span className="block text-sm font-medium text-gray-900">
                {SCOPE_LABELS[scope]?.title || scope}
              </span>
              <span className="block text-sm text-gray-600">
                {SCOPE_LABELS[scope]?.detail || ''}
              </span>
            </label>
          </li>
        ))}
      </ul>

      <p className="mt-6 text-sm text-gray-600">
        Approving sends a one-time code to <span className="font-mono">{details.redirectHost}</span>.
        If you do not recognise that, do not continue.
      </p>

      <div className="mt-6 flex gap-3">
        <button
          type="button"
          disabled={busy || !chosen.length}
          className="rounded bg-indigo-600 px-4 py-2 text-white disabled:opacity-50"
          onClick={async () => {
            setBusy(true);
            finish(await approve({ request, approvedScopes: chosen }));
          }}
        >
          Allow
        </button>
        <button
          type="button"
          disabled={busy}
          className="rounded border border-gray-300 px-4 py-2 text-gray-700 disabled:opacity-50"
          onClick={async () => {
            setBusy(true);
            finish(await deny({ request }));
          }}
        >
          Deny
        </button>
      </div>
    </Shell>
  );
}

function Shell(props: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-lg rounded-lg bg-white p-8 shadow">
        {props.children}
      </div>
    </div>
  );
}
